import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Button, Checkbox, FormControlLabel, Autocomplete, TextField, Typography, Alert,
} from '@mui/material';
import { newTickerCount } from '../utils/listEdits';

/**
 * The confirmations behind list editing (see hooks/useListEditing): merge two
 * lists, pick where to move tickers, and the "that would empty the list" case.
 */
export default function ListEditDialogs({ editing, lists }) {
  const { dialog, closeDialog, confirmMerge, applyMove } = editing;
  const [deleteSource, setDeleteSource] = useState(true);
  const [copy, setCopy] = useState(false);
  const [target, setTarget] = useState(null);

  useEffect(() => {
    setDeleteSource(true);
    setCopy(false);
    setTarget(null);
  }, [dialog]);

  const targets = useMemo(
    () => Object.keys(lists || {}).filter(n => !dialog || n !== dialog.from).sort((a, b) => a.localeCompare(b)),
    [lists, dialog]
  );

  if (!dialog) return null;

  if (dialog.kind === 'merge') {
    const { source, target: into } = dialog;
    const src = lists[source] || [];
    const tgt = lists[into] || [];
    const added = newTickerCount(tgt, src);
    return (
      <Dialog open onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Merge lists?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" sx={{ mb: 1 }}>
              Merge <strong>{source}</strong> ({src.length}) into <strong>{into}</strong> ({tgt.length}).
            </Typography>
            <Typography variant="body2">
              <strong>{into}</strong> will have {tgt.length + added} tickers
              {added < src.length ? ` (${src.length - added} of ${source} were already in it)` : ''}.
            </Typography>
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={deleteSource} onChange={(e) => setDeleteSource(e.target.checked)} />}
            label={`Delete '${source}' afterwards`}
          />
          <Alert severity="warning" sx={{ mt: 1 }} variant="outlined">
            This changes your saved lists. You can undo it for a few seconds afterwards.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => { closeDialog(); confirmMerge(source, into, deleteSource); }}
          >
            Merge
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (dialog.kind === 'pick') {
    const { from, tickers } = dialog;
    return (
      <Dialog open onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Move {tickers.length} ticker{tickers.length === 1 ? '' : 's'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            {tickers.length <= 4 ? tickers.join(', ') : `${tickers.slice(0, 4).join(', ')} and ${tickers.length - 4} more`} from <strong>{from}</strong> to:
          </DialogContentText>
          <Autocomplete
            autoFocus
            size="small"
            options={targets}
            value={target}
            onChange={(_, v) => setTarget(v)}
            renderInput={(params) => <TextField {...params} label="List" autoFocus />}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={copy} onChange={(e) => setCopy(e.target.checked)} />}
            label={`Keep them in '${from}' too (copy)`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!target}
            onClick={() => { closeDialog(); applyMove(from, target, tickers, copy); }}
          >
            {copy ? 'Copy' : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (dialog.kind === 'emptied') {
    const { from, to, tickers, copy: wasCopy } = dialog;
    return (
      <Dialog open onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>That empties “{from}”</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {to
              ? `Moving ${tickers.length === 1 ? tickers[0] : `these ${tickers.length} tickers`} to '${to}' leaves '${from}' with no tickers, and a list cannot be empty.`
              : `Removing ${tickers.length === 1 ? tickers[0] : `these ${tickers.length} tickers`} leaves '${from}' with no tickers, and a list cannot be empty.`}
            {' '}Delete the list as well?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Keep the list</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => { closeDialog(); applyMove(from, to, tickers, wasCopy, { confirmedEmpty: true }); }}
          >
            Delete “{from}”
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return null;
}
